import { Crown, Timer, X } from '@/components/ui/icons';
import { useMemo } from 'react';
import { isTimedOut, type MemberDTO, type PresenceStatus, type PublicUser, type RoleDTO } from '@clanfire/shared';
import { StaffBadge } from '@/components/profile/StaffBadge';
import { StyledName } from '@/components/profile/StyledName';
import { Avatar, statusLabel } from '@/components/ui/Avatar';
import { IconButton } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';
import { roleColor, useMemberRoles, useServerMembers, useServerRoles, useServers, withServerProfile } from '@/stores/servers';
import { useUi } from '@/stores/ui';
import { usePresence, useUsers } from '@/stores/users';

interface Row {
  member: MemberDTO;
  user: PublicUser;
  presence: PresenceStatus;
}

export function MemberList({ serverId, onClose }: { serverId: string; onClose?: () => void }) {
  const members = useServerMembers(serverId);
  const roles = useServerRoles(serverId);
  const state = useServers((s) => s.membersState[serverId]);
  const ownerId = useServers((s) => s.servers[serverId]?.ownerId);
  const presences = usePresence((s) => s.statuses);
  const users = useUsers((s) => s.users);

  const groups = useMemo(() => {
    const rows: Row[] = members.map((m) => ({ member: m, user: users[m.userId] ?? m.user, presence: presences[m.userId] ?? m.presence }));
    const nameOf = (r: Row) => r.member.nickname || r.user.displayName;
    const byName = (a: Row, b: Row) => nameOf(a).localeCompare(nameOf(b));
    const online = rows.filter((r) => r.presence !== 'offline');
    const offline = rows.filter((r) => r.presence === 'offline').sort(byName);
    // Online members are listed under their highest role shown separately (`roles` is
    // sorted highest first); everyone else under "Online".
    const defaultRole = roles.find((r) => r.isDefault);
    const topRoleId = (r: Row) => roles.find((role) => role.hoist && r.member.roleIds.includes(role.id))?.id ?? defaultRole?.id;
    const result: Array<{ title: string; role?: RoleDTO; rows: Row[] }> = [];
    for (const role of roles) {
      const inRole = online.filter((r) => topRoleId(r) === role.id).sort(byName);
      if (inRole.length) result.push({ title: role.isDefault ? 'Online' : role.name, role, rows: inRole });
    }
    if (offline.length) result.push({ title: 'Offline', rows: offline });
    return result;
  }, [members, roles, presences, users]);

  return (
    <div data-cf="member-list" className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-16 shrink-0 items-center justify-between px-5">
        <h2 className="text-lg text-fg">
          Members{state === 'loaded' && <span className="ml-1.5 font-sans text-sm text-fg-muted">{members.length}</span>}
        </h2>
        {onClose && (
          <IconButton label="Close member list" size="sm" onClick={onClose}>
            <X size={18} />
          </IconButton>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {state === 'error' ? (
          <ErrorState message="Could not load members." onRetry={() => void useServers.getState().loadMembers(serverId, true)} />
        ) : state !== 'loaded' ? (
          <div className="flex flex-col gap-3 px-2">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-3.5 flex-1" />
              </div>
            ))}
          </div>
        ) : (
          groups.map((group) => (
            <section key={group.title + (group.role?.id ?? '')} className="mb-3" aria-label={group.title}>
              <h3 className="px-2 pt-2 pb-1 font-sans text-[11px] font-semibold tracking-[0.08em] text-fg-muted uppercase">
                {group.title} — {group.rows.length}
              </h3>
              <ul>
                {group.rows.map((row) => (
                  <MemberRow key={row.member.userId} row={row} serverId={serverId} isOwner={row.member.userId === ownerId} />
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </div>
  );
}

function MemberRow({ row, serverId, isOwner }: { row: Row; serverId: string; isOwner: boolean }) {
  const openModal = useUi((s) => s.openModal);
  const nameColor = roleColor(useMemberRoles(serverId, row.member.userId));
  const offline = row.presence === 'offline';
  return (
    <li>
      <button
        type="button"
        onClick={() => openModal({ type: 'profile', userId: row.member.userId, serverId })}
        className={cn('flex w-full items-center gap-2.5 rounded-full p-1.5 text-left transition-colors hover:bg-hover', offline && 'opacity-55 hover:opacity-100')}
      >
        <Avatar user={withServerProfile(row.user, row.user.blazeSince ? row.member.profile : null)} size={34} status={row.presence} ring="ring-panel" />
        <span className="min-w-0 flex-1 leading-tight">
          <span className="flex items-center gap-1">
            <StyledName user={row.user} fallbackColor={nameColor} className="truncate text-[13px] font-semibold">
              {row.member.nickname || row.user.displayName}
            </StyledName>
            {isOwner && <Crown size={13} className="shrink-0 text-warning" aria-label="Server owner" />}
            {isTimedOut(row.member.timeoutUntil) && <Timer size={13} className="shrink-0 text-danger" aria-label="Timed out" />}
            <StaffBadge role={row.user.staffRole} />
          </span>
          <span className="block truncate text-[11px] text-fg-muted">
            {row.user.customStatus && !offline ? `${row.user.customStatusEmoji ?? ''} ${row.user.customStatus}`.trim() : statusLabel[row.presence]}
          </span>
        </span>
      </button>
    </li>
  );
}
